import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BarChart3,
  Clock,
  Crosshair,
  List,
  TrendingUp,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useChartTheme } from "@/shared/hooks/useChartTheme";
import { ChartTooltip } from "@/shared/ui/ChartTooltip";
import { cn } from "@/shared/lib/cn";
import { resolveChartColor, resolveChartData } from "@/shared/lib/resolveChartColor";
import {
  statisticsAdmissionStatuses,
  statisticsInvoiceStatuses,
  statisticsMonthlyInvoiced,
  statisticsMonthlyTrend,
  statisticsTopMedicalActs,
} from "../../data/statisticsDashboard";
import { statisticsChartRoutes } from "../../navigation/dashboardRoutes";

type ChartCardProps = {
  title: string;
  icon: ReactNode;
  badge?: string;
  badgeClassName?: string;
  href?: string;
  children: ReactNode;
};

function ChartCard({ title, icon, badge, badgeClassName, href, children }: ChartCardProps) {
  const content = (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {icon}
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {badge && (
          <span
            className={cn(
              "shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              badgeClassName,
            )}
          >
            {badge}
          </span>
        )}
      </div>
      {children}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="theme-card block p-5 transition-all hover:border-accent/40 hover:shadow-[var(--shadow)]"
      >
        {content}
      </Link>
    );
  }

  return <div className="theme-card theme-card-interactive p-5">{content}</div>;
}

function DonutLegend({
  items,
  labels,
  isDark,
}: {
  items: { key: string; color: string }[];
  labels: Record<string, string>;
  isDark: boolean;
}) {
  return (
    <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-2">
      {items.map((item) => (
        <span key={item.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: resolveChartColor(item.color, isDark) }}
          />
          {labels[item.key]}
        </span>
      ))}
    </div>
  );
}

function DonutChart({
  data,
  labels,
  isDark,
}: {
  data: { key: string; value: number; color: string }[];
  labels: Record<string, string>;
  isDark: boolean;
}) {
  const chartData = resolveChartData(
    data
      .filter((item) => item.value > 0)
      .map((item) => ({
        ...item,
        name: labels[item.key],
      })),
    isDark,
  );

  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={52}
            outerRadius={78}
            dataKey="value"
            stroke="none"
            paddingAngle={2}
          >
            {chartData.map((entry) => (
              <Cell key={entry.key} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0];
              const pct = total > 0 ? (((item.value as number) / total) * 100).toFixed(1) : "0";
              return (
                <div className="chart-tooltip px-3 py-2 text-foreground">
                  <p className="text-xs font-medium">
                    {item.name}: {item.value} ({pct}%)
                  </p>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <DonutLegend items={data} labels={labels} isDark={isDark} />
    </>
  );
}

export function StatisticsTrendsSection({
  monthlyTrend,
  monthlyInvoiced,
}: {
  monthlyTrend?: Array<{ month: string; value: number }>;
  monthlyInvoiced?: Array<{ month: string; value: number }>;
} = {}) {
  const admissionsTrend = monthlyTrend ?? statisticsMonthlyTrend;
  const invoicedTrend = monthlyInvoiced ?? statisticsMonthlyInvoiced;
  const { t } = useTranslation();
  const { tick, grid, colors, isDark } = useChartTheme();
  const labels = t.safecheck.statistics.trends;

  return (
    <section className="mt-8">
      <h2 className="mb-4 text-[11px] font-semibold uppercase tracking-wider text-muted">
        {labels.sectionTitle}
      </h2>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title={labels.monthlyAdmissions}
          href={statisticsChartRoutes.monthlyAdmissions}
          badge={labels.last12Months}
          badgeClassName="bg-purple-soft text-purple"
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-soft">
              <TrendingUp size={16} className="text-purple" />
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={admissionsTrend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="statsAdmissionsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.navy} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={colors.navy} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 10 }}
                interval={0}
                angle={-35}
                textAnchor="end"
                height={50}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 11 }}
                domain={[0, 350]}
                ticks={[0, 35, 70, 105, 140, 175, 210, 245, 280, 315, 350]}
                width={36}
              />
              <Tooltip content={<ChartTooltip />} />
              <Area
                type="monotone"
                dataKey="value"
                name={labels.monthlyAdmissions}
                stroke={colors.navy}
                strokeWidth={2.5}
                fill="url(#statsAdmissionsGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={labels.monthlyInvoiced}
          href={statisticsChartRoutes.monthlyInvoiced}
          badge={labels.months12}
          badgeClassName="bg-teal-soft text-teal"
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-soft">
              <BarChart3 size={16} className="text-teal" />
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={invoicedTrend} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 10 }}
                interval={0}
                angle={-35}
                textAnchor="end"
                height={50}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 11 }}
                domain={[0, 1]}
                ticks={[0, 0.25, 0.5, 0.75, 1]}
                width={36}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" fill={colors.lime} radius={[4, 4, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          title={labels.admissionStatuses}
          href={statisticsChartRoutes.admissionStatuses}
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-soft">
              <Clock size={16} className="text-yellow" />
            </div>
          }
        >
          <DonutChart data={statisticsAdmissionStatuses} labels={labels.admissionStatusLabels} isDark={isDark} />
        </ChartCard>

        <ChartCard
          title={labels.invoiceStatuses}
          href={statisticsChartRoutes.invoiceStatuses}
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft">
              <Crosshair size={16} className="text-primary" />
            </div>
          }
        >
          <DonutChart data={statisticsInvoiceStatuses} labels={labels.invoiceStatusLabels} isDark={isDark} />
        </ChartCard>

        <ChartCard
          title={labels.topMedicalActs}
          href={statisticsChartRoutes.topMedicalActs}
          badge={labels.byFrequency2026}
          badgeClassName="bg-danger-soft text-danger"
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-pink-soft">
              <List size={16} className="text-pink" />
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={statisticsTopMedicalActs}
              layout="vertical"
              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 11 }}
                domain={[0, 1]}
                ticks={[0, 0.25, 0.5, 0.75, 1]}
              />
              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 11 }}
                width={8}
                hide={statisticsTopMedicalActs.length === 0}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" fill={colors.navy} radius={[0, 4, 4, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </section>
  );
}
